import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { jwtDecode } from 'jwt-decode';
import { getV2Token } from '../utils/v2AuthUtils';
import SecureCookieManager from '../utils/secureCookie';
import { getMyTenant, MyTenant } from '../services/tenantServices';
import authService from '../services/authService';
import { goToDccLogin } from '../utils/dccLogin';

const VIEW_AS_STORAGE_KEY = 'comply.viewAsTenantId';

interface JwtTenantClaims {
  tenantId?: string;
  tenantPath?: string;
  tenantDepth?: number;
  allowedScopes?: Array<'own' | 'subtree' | 'ancestor'>;
}

interface TenantContextValue {
  /** Effective tenant for the current request (reflects view-as). */
  currentTenant: MyTenant | null;
  /** TenantId the user actually logged in as, from the JWT. Never changes during a session. */
  actualTenantId: string | null;
  /** True when the logged-in user's tenant is a root tenant (or has subtree scope). */
  isRoot: boolean;
  /** True when the user is currently impersonating another tenant via view-as. */
  isViewingAs: boolean;
  /** The tenantId being impersonated, or null. */
  viewAsTenantId: string | null;
  isLoading: boolean;
  /** Start impersonating a tenant. Triggers a reload so all data refetches under the new context. */
  setViewAs: (tenantId: string) => void;
  /** Stop impersonating. Reloads to refresh data. */
  clearViewAs: () => void;
  /** Manual refresh of /tenant/me. */
  refresh: () => Promise<void>;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

function readToken(): string | null {
  return SecureCookieManager.getAuthToken() || getV2Token() || null;
}

function decodeTenantClaims(): JwtTenantClaims | null {
  const token = readToken();
  if (!token) return null;
  try {
    return jwtDecode<JwtTenantClaims>(token);
  } catch {
    return null;
  }
}

export function getViewAsTenantId(): string | null {
  try {
    return sessionStorage.getItem(VIEW_AS_STORAGE_KEY);
  } catch {
    return null;
  }
}

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentTenant, setCurrentTenant] = useState<MyTenant | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [viewAsTenantId, setViewAsTenantIdState] = useState<string | null>(
    () => getViewAsTenantId(),
  );
  // Bumped by the `comply:auth-changed` event (dispatched by login + logout)
  // so the provider re-decodes the JWT and re-fetches /tenant/me without a
  // full page reload. Without this the sidebar shows the non-partner menu
  // after a partner login until the user manually refreshes.
  const [authVersion, setAuthVersion] = useState(0);

  const claims = useMemo(() => decodeTenantClaims(), [authVersion]);
  const actualTenantId = claims?.tenantId ?? null;
  // Root = user has subtree access. That's the only role that can view-as.
  const isRoot = Boolean(claims?.allowedScopes?.includes('subtree'));

  const fetchMe = useCallback(async () => {
    if (!readToken()) {
      setCurrentTenant(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const me = await getMyTenant();
      setCurrentTenant(me);

      // If the *actual* logged-in tenant has been deactivated, force logout.
      // We never kick out a root user who is only viewing-as an inactive
      // child — their own session is still legitimate.
      const viewing = getViewAsTenantId();
      const isViewing = Boolean(
        viewing && claims?.tenantId && viewing !== claims.tenantId,
      );
      if (me && me.isActive === false && !isViewing && !(window as any).__LOGGING_OUT__) {
        try {
          sessionStorage.setItem('comply.inactiveTenantLogout', '1');
        } catch {}
        await authService.logout();
        goToDccLogin('tenant_inactive');
        return;
      }
    } catch {
      // /tenant/me may 401 or 404 in early bootstrap; ignore.
      setCurrentTenant(null);
    } finally {
      setIsLoading(false);
    }
  }, [claims?.tenantId]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe, authVersion]);

  // React to login / logout without requiring a page refresh. The login
  // page + logout service dispatch `comply:auth-changed` after writing
  // the token; we bump `authVersion`, which re-decodes the JWT and
  // re-runs fetchMe(), and the Sidebar re-renders with the new archetype.
  useEffect(() => {
    const onAuthChanged = () => setAuthVersion((v) => v + 1);
    window.addEventListener('comply:auth-changed', onAuthChanged);
    // Storage event is the cross-tab signal (token cookies don't fire
    // storage events but localStorage tokens do).
    window.addEventListener('storage', onAuthChanged);
    return () => {
      window.removeEventListener('comply:auth-changed', onAuthChanged);
      window.removeEventListener('storage', onAuthChanged);
    };
  }, []);

  const setViewAs = useCallback((tenantId: string) => {
    try {
      sessionStorage.setItem(VIEW_AS_STORAGE_KEY, tenantId);
    } catch {}
    setViewAsTenantIdState(tenantId);
    // Full reload so every query refetches under the new tenant context.
    // Cheaper and more reliable than invalidating ad-hoc caches across pages.
    window.location.reload();
  }, []);

  const clearViewAs = useCallback(() => {
    try {
      sessionStorage.removeItem(VIEW_AS_STORAGE_KEY);
    } catch {}
    setViewAsTenantIdState(null);
    window.location.reload();
  }, []);

  const isViewingAs = Boolean(
    viewAsTenantId &&
      actualTenantId &&
      viewAsTenantId !== actualTenantId,
  );

  const value: TenantContextValue = {
    currentTenant,
    actualTenantId,
    isRoot,
    isViewingAs,
    viewAsTenantId,
    isLoading,
    setViewAs,
    clearViewAs,
    refresh: fetchMe,
  };

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
};

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error('useTenant must be used inside <TenantProvider>');
  }
  return ctx;
}
